import { asId, type AreaId, type PersonId, type SubAreaId } from '@shared/brand';
import { GLOBAL_AREA_SENTINEL, type AuditEntry } from '@domain/entities/AuditEntry';
import type { Balance } from '@domain/entities/Balance';
import type { Holiday } from '@domain/entities/Holiday';
import { customaryHolidaysFor, statutoryHolidaysFor } from '@domain/entities/Holiday';
import type { Incapacity } from '@domain/entities/Incapacity';
import type { Area, Company, SubArea } from '@domain/entities/Organization';
import type { Position } from '@domain/entities/RuntimeConfig';
import { seedColorForSubArea } from '@domain/entities/Organization';
import type { PermissionRequest } from '@domain/entities/PermissionRequest';
import type { EmergencyContact, Person, PersonLeaderLink } from '@domain/entities/Person';
import { DEFAULT_NOTIFICATION_PREFERENCES } from '@domain/entities/Person';
import type {
  ReplacementDecision,
  RequestApproval,
  RequestWorkItem,
  VacationRequest,
} from '@domain/entities/VacationRequest';
import { periodFor, periodYearOf } from '@domain/policies/VacationPeriodPolicy';
import { CalendarDate } from '@domain/value-objects/CalendarDate';
import { DateRange } from '@domain/value-objects/DateRange';

/**
 * Sample data for demo mode, taken from the design handoff's own fixtures.
 *
 * Using the prototype's people, folios, dates and audit entries rather than invented
 * ones matters: it means a screen rendered here can be compared side by side with the
 * design, and any difference is a real difference rather than a data mismatch.
 *
 * Dates are anchored to the handoff's "today" (27 July 2026) so the relative states —
 * what is upcoming, what has been taken, what is expiring — stay coherent regardless
 * of when this runs.
 */

export const DEMO_TODAY = CalendarDate.fromISOOrThrow('2026-07-27');

const stamp = (iso: string): Date => new Date(`${iso}T09:00:00Z`);

const audit = {
  createdAt: stamp('2026-07-01'),
  updatedAt: stamp('2026-07-27'),
  schemaVersion: 1,
};

const area = (raw: string): AreaId => asId<'AreaId'>(raw);
const sub = (raw: string): SubAreaId => asId<'SubAreaId'>(raw);
const person = (raw: string): PersonId => asId<'PersonId'>(raw);

export const AREA_TECNOLOGIA = area('tecnologia');
export const AREA_ACADEMICO = area('academico');

export const SUB_IOS = sub('tecnologia_desarrollo-ios');
export const SUB_FLUTTER = sub('tecnologia_desarrollo-flutter');
export const SUB_BACKEND = sub('tecnologia_backend');
export const SUB_PO = sub('tecnologia_pos');
export const SUB_QA = sub('tecnologia_qa');

export const P_MARCO = person('p-marco');
export const P_LUCIA = person('p-lucia');
export const P_ANA = person('p-ana');
export const P_BRUNO = person('p-bruno');
export const P_DIEGO = person('p-diego');
export const P_SOFIA = person('p-sofia');
export const P_ROCIO = person('p-rocio');
export const P_IVAN = person('p-ivan');
export const P_ELENA = person('p-elena');
export const P_RENZO = person('p-renzo');

export const DEMO_COMPANY: Company = {
  ...audit,
  id: asId<'CompanyId'>('knotion'),
  name: 'Knotion',
  allowedDomains: ['knotion.com'],
  hrEmail: 'personas@knotion.com',
  country: 'México',
  logoPath: null,
};

export const DEMO_AREAS: readonly Area[] = [
  {
    ...audit,
    id: AREA_TECNOLOGIA,
    companyId: DEMO_COMPANY.id,
    name: 'Tecnología',
    color: '#4A6FA5',
    active: true,
    adminEmail: 'personas@knotion.com',
  },
  {
    ...audit,
    id: AREA_ACADEMICO,
    companyId: DEMO_COMPANY.id,
    name: 'Académico',
    color: '#12A594',
    active: true,
    adminEmail: 'academico@knotion.com',
  },
  {
    ...audit,
    id: area('contenidos'),
    companyId: DEMO_COMPANY.id,
    name: 'Creación de contenidos',
    color: '#E8930C',
    active: true,
    adminEmail: 'contenidos@knotion.com',
  },
  {
    ...audit,
    id: area('comercial'),
    companyId: DEMO_COMPANY.id,
    name: 'Comercial',
    color: '#7C5CFF',
    active: true,
    adminEmail: 'comercial@knotion.com',
  },
  {
    ...audit,
    id: area('finanzas'),
    companyId: DEMO_COMPANY.id,
    name: 'Finanzas',
    color: '#65748B',
    active: false,
    adminEmail: '',
  },
];

const makeSubArea = (
  id: SubAreaId,
  name: string,
  maxConcurrentOut: number,
  leadPersonId: PersonId | null,
): SubArea => ({
  ...audit,
  id,
  areaId: AREA_TECNOLOGIA,
  name,
  maxConcurrentOut,
  leadPersonId,
  color: seedColorForSubArea(name),
});

export const DEMO_SUB_AREAS: readonly SubArea[] = [
  makeSubArea(SUB_IOS, 'Desarrollo iOS', 2, P_ANA),
  makeSubArea(SUB_FLUTTER, 'Desarrollo Flutter', 2, P_DIEGO),
  makeSubArea(SUB_BACKEND, 'Backend', 3, P_ROCIO),
  makeSubArea(SUB_PO, "PO's", 1, P_ELENA),
  makeSubArea(SUB_QA, 'QA', 2, P_IVAN),
];

/**
 * Puestos de muestra, los del área de Tecnología.
 *
 * Existen para que el selector del alta ofrezca algo reconocible en vez de una
 * lista vacía. **No son el catálogo real** — ése lo tiene RR.HH. y sale de los
 * contratos, no de lo que aquí se imagine. Mismo error que ya se pagó con las
 * áreas inventadas por el seeder.
 */
export const DEMO_POSITIONS: readonly Position[] = [
  { id: 'dev-ios', title: 'Desarrollador iOS', enabled: true },
  { id: 'dev-flutter', title: 'Desarrollador Flutter', enabled: true },
  { id: 'dev-backend', title: 'Desarrollador Backend', enabled: true },
  { id: 'product-owner', title: 'Product Owner', enabled: true },
  { id: 'qa', title: 'Analista de QA', enabled: true },
  { id: 'lider-tec', title: 'Líder de Tecnología', enabled: true },
  // Apagado a propósito: el alta no debe ofrecerlo y las fichas que lo tengan
  // siguen leyéndolo. Es la razón por la que el catálogo apaga en vez de borrar.
  { id: 'becario', title: 'Becario', enabled: false },
];

/**
 * Cumpleaños repartidos, para que Kn·Blog tenga qué celebrar en el demo.
 *
 * Todos el mismo día —como estaban— dejaban la pantalla con un solo caso. Así hay
 * tres que cumplen «hoy» (`DEMO_TODAY`, 27 de julio) —Marco entre ellos, para ver
 * la felicitación—, varios más en julio, uno el 29 de febrero y el resto
 * repartidos por el año.
 */
const DEMO_BIRTH_DATES: Readonly<Record<string, string>> = {
  /* Marco es quien entra por omisión: así el demo enseña la felicitación. */
  'p-marco': '1991-07-27',
  'p-lucia': '1996-07-27',
  'p-diego': '1988-07-27',
  'p-ana': '1985-07-03',
  'p-bruno': '1999-07-15',
  'p-sofia': '1992-07-30',
  'p-rocio': '1992-02-29',
  'p-ivan': '1987-08-02',
  'p-elena': '1983-11-19',
};

const makePerson = (
  id: PersonId,
  displayName: string,
  email: string,
  subAreaId: SubAreaId,
  role: Person['role'],
  hireDate: string,
  status: Person['status'] = 'active',
  /*
   * El puesto, que el demo tenía en `null` para todo el mundo.
   *
   * Contra producción son 327 de 339 las que sí lo tienen, así que el demo estaba
   * enseñando el caso raro como si fuera el normal — y desde que la ficha lo pinta,
   * enseñándolo vacío. Renzo se queda sin puesto a propósito: es la cuenta pendiente
   * de validar y el único sitio donde se ve qué hace la ficha sin ese dato.
   */
  positionId: string | null = null,
  /*
   * El expediente, que el demo tenía vacío para todo el mundo.
   *
   * Con la carga de RR.HH. del 22 sep 2026, 287 fichas de producción llevan al
   * menos el acta, así que «sin un solo papel» dejó de ser el caso normal — y el
   * visor de documentos no se podía ni mirar en el demo, porque no había nada
   * que abrir. Marco los lleva; los demás siguen vacíos, que es el otro caso.
   */
  documents: Person['documents'] = {},
): Person => ({
  ...audit,
  id,
  documents,
  workMode: null,
  computerEquipment: '',
  extraPermissions: [],
  revokedPermissions: [],
  /* Con dato, no en blanco: el demo existe para mirar la pantalla llena.
     Ver `DEMO_ADMIN` y compañía, que sí van vacías — son el otro caso. */
  birthDate: CalendarDate.fromISOOrThrow(DEMO_BIRTH_DATES[id] ?? '1991-03-14'),
  shirtSize: 'M',
  parentRole: 'mama',
  uid: `uid-${id}`,
  email,
  displayName,
  // El padrón sembrado es anterior al alta estructurada, igual que las 37 de
  // producción: nombre compuesto y sin partes. Es el caso que hay que poder ver.
  nameParts: null,
  positionId,
  termination: null,
  areaId: AREA_TECNOLOGIA,
  subAreaId,
  role,
  status,
  hireDate: CalendarDate.fromISOOrThrow(hireDate),
  entitledDaysOverride: null,
  photoURL: null,
  notificationPrefs: DEFAULT_NOTIFICATION_PREFERENCES,
});

/** Cuatro papeles ya cargados, con la forma de ruta que usa Storage de verdad. */
const expediente = (
  personId: string,
  tipos: readonly string[],
): Person['documents'] =>
  Object.fromEntries(
    tipos.map((tipo) => [
      tipo,
      {
        storagePath: `expedientes/tecnologia/${personId}/${tipo}/documento.pdf`,
        uploadedAt: new Date('2026-09-22T17:30:00Z'),
        uploadedByPersonId: null,
      },
    ]),
  );

export const DEMO_PEOPLE: readonly Person[] = [
  // Renueva el 10 de marzo. Con «Vencimiento del saldo» en 18 meses, lo que le
  // queda del periodo 2025 vale hasta el 9 de septiembre de 2026 —a 44 días del
  // «hoy» del demo— junto a su periodo 2026: es lo que deja dos periodos encimados
  // y el aviso de vencimiento dentro de la ventana, visibles en Inicio.
  makePerson(
    P_MARCO,
    'Marco Rivas',
    'mrivas@knotion.com',
    SUB_IOS,
    'empleado',
    '2019-03-10',
    'active',
    'dev-ios',
    expediente('p-marco', ['acta', 'curp', 'ine', 'domicilio']),
  ),
  makePerson(P_LUCIA, 'Lucía Fernández', 'lfernandez@knotion.com', SUB_IOS, 'empleado', '2023-02-01', 'active', 'dev-ios'),
  makePerson(P_ANA, 'Ana Cifuentes', 'acifuentes@knotion.com', SUB_IOS, 'lider', '2016-08-22', 'active', 'lider-tec'),
  makePerson(P_BRUNO, 'Bruno Chávez', 'bchavez@knotion.com', SUB_IOS, 'empleado', '2021-11-08', 'active', 'becario'),
  makePerson(P_DIEGO, 'Diego Salas', 'dsalas@knotion.com', SUB_FLUTTER, 'lider', '2018-03-05', 'active', 'lider-tec'),
  makePerson(P_SOFIA, 'Sofía Ballón', 'sballon@knotion.com', SUB_FLUTTER, 'empleado', '2014-09-30', 'active', 'dev-flutter'),
  makePerson(P_ROCIO, 'Rocío Paz', 'rpaz@knotion.com', SUB_BACKEND, 'lider', '2020-01-13', 'active', 'dev-backend'),
  makePerson(P_IVAN, 'Iván Mora', 'imora@knotion.com', SUB_QA, 'lider', '2017-06-19', 'active', 'qa'),
  makePerson(P_ELENA, 'Elena Ruiz', 'eruiz@knotion.com', SUB_PO, 'lider', '2015-05-04', 'active', 'product-owner'),
  // The handoff's "Cuentas por validar" queue needs at least one pending account.
  makePerson(P_RENZO, 'Renzo Aguilar', 'raguilar@knotion.com', SUB_BACKEND, 'empleado', '2026-07-20', 'pending'),
];

/** RR.HH. — an area administrator, so the admin screens have a role to render under. */
export const P_RENATO = person('p-renato');

export const DEMO_ADMIN: Person = {
  ...audit,
  nameParts: null,
  positionId: null,
  termination: null,
  birthDate: null,
  shirtSize: null,
  parentRole: null,
  id: P_RENATO,
  uid: 'uid-renato',
  email: 'personas@knotion.com',
  displayName: 'Renato Vidal',
  areaId: AREA_TECNOLOGIA,
  subAreaId: null,
  role: 'admin',
  documents: {},
  workMode: null,
  computerEquipment: '',
  extraPermissions: [],
  revokedPermissions: [],
  status: 'active',
  hireDate: CalendarDate.fromISOOrThrow('2013-02-11'),
  entitledDaysOverride: null,
  photoURL: null,
  notificationPrefs: DEFAULT_NOTIFICATION_PREFERENCES,
};

/** Finanzas: ve y fija salarios, y nada más. Para poder ejercitar ese rol. */
export const P_FABIOLA = person('p-fabiola');

export const DEMO_FINANZAS: Person = {
  ...audit,
  nameParts: null,
  positionId: null,
  termination: null,
  birthDate: null,
  shirtSize: null,
  parentRole: null,
  id: P_FABIOLA,
  uid: 'uid-fabiola',
  email: 'ffranco@knotion.com',
  displayName: 'Fabiola Franco',
  areaId: AREA_TECNOLOGIA,
  subAreaId: null,
  role: 'finanzas',
  documents: {},
  workMode: null,
  computerEquipment: '',
  extraPermissions: [],
  revokedPermissions: [],
  status: 'active',
  hireDate: CalendarDate.fromISOOrThrow('2021-04-05'),
  entitledDaysOverride: null,
  photoURL: null,
  notificationPrefs: DEFAULT_NOTIFICATION_PREFERENCES,
};

/**
 * Administrador global, para el panorama de toda la empresa.
 *
 * Vive en un área —Dirección general— porque el rol no obliga a no tenerla: lo
 * que le da el alcance es el rol, no la ausencia de área. Ver `inScope` en las
 * reglas.
 */
export const P_GLORIA = person('p-gloria');

export const DEMO_GLOBAL: Person = {
  ...audit,
  nameParts: null,
  positionId: null,
  termination: null,
  birthDate: null,
  shirtSize: null,
  parentRole: null,
  id: P_GLORIA,
  uid: 'uid-gloria',
  email: 'gloria.duarte@knotion.com',
  displayName: 'Gloria Duarte',
  areaId: AREA_TECNOLOGIA,
  subAreaId: null,
  role: 'global',
  documents: {},
  workMode: null,
  computerEquipment: '',
  /*
   * El espejo de quien tiene este rol en producción, que es quien pidió el
   * organigrama. `areas` allá le llega por la matriz viva —se le dio al rol
   * entero el 28 sep 2026— y aquí por excepción, que para el demo es lo mismo:
   * sin él no vería «Áreas de la empresa», que es donde está el botón.
   * `editar_organigrama` es el permiso que se da persona por persona, y es
   * justo lo que se está probando: la Super del demo **no** lo tiene, y ve el
   * organigrama sin poder moverlo.
   */
  extraPermissions: ['areas', 'editar_organigrama'],
  revokedPermissions: [],
  status: 'active',
  hireDate: CalendarDate.fromISOOrThrow('2020-02-17'),
  entitledDaysOverride: null,
  photoURL: null,
  notificationPrefs: DEFAULT_NOTIFICATION_PREFERENCES,
};

/** Superuser, for the system screens. */
export const P_RENATA = person('p-renata');

export const DEMO_SUPERUSER: Person = {
  ...audit,
  nameParts: null,
  positionId: null,
  termination: null,
  birthDate: null,
  shirtSize: null,
  parentRole: null,
  id: P_RENATA,
  uid: 'uid-renata',
  email: 'renata@knotion.com',
  displayName: 'Renata Ochoa',
  areaId: AREA_TECNOLOGIA,
  subAreaId: null,
  role: 'super',
  documents: {},
  workMode: null,
  computerEquipment: '',
  extraPermissions: [],
  revokedPermissions: [],
  status: 'active',
  hireDate: CalendarDate.fromISOOrThrow('2012-01-09'),
  entitledDaysOverride: null,
  photoURL: null,
  notificationPrefs: DEFAULT_NOTIFICATION_PREFERENCES,
};

export const DEMO_ALL_PEOPLE: readonly Person[] = [
  DEMO_FINANZAS,
  DEMO_GLOBAL,
  ...DEMO_PEOPLE,
  DEMO_ADMIN,
  DEMO_SUPERUSER,
];

const link = (
  personId: PersonId,
  leaderPersonId: PersonId,
  projectLabel: string,
): PersonLeaderLink => ({
  ...audit,
  id: `${personId}_${leaderPersonId}`,
  personId,
  leaderPersonId,
  areaId: AREA_TECNOLOGIA,
  projectLabel,
});

/**
 * Each leader's project, from the prototype's `LEADS` table.
 *
 * The label is what makes a double sign-off legible: without it, "Ana approves
 * and Rocío also approves" reads as redundancy rather than as two projects the
 * person is genuinely committed to.
 */
export const DEMO_LEADER_PROJECTS: Readonly<Record<string, string>> = {
  [P_ANA]: 'App 4.2 iOS',
  [P_DIEGO]: 'Onboarding Flutter',
  [P_ROCIO]: 'Migración Swift 6',
  [P_IVAN]: 'Certificación QA 4.2',
  [P_ELENA]: 'Roadmap de producto',
};

/** Marco and Lucía report to two leaders each, which triggers the double sign-off. */
export const DEMO_LEADER_LINKS: readonly PersonLeaderLink[] = [
  link(P_MARCO, P_ANA, 'App 4.2 iOS'),
  link(P_MARCO, P_ROCIO, 'Migración Swift 6'),
  link(P_LUCIA, P_ANA, 'App 4.2 iOS'),
  link(P_LUCIA, P_IVAN, 'Certificación QA 4.2'),
  link(P_BRUNO, P_ANA, 'App 4.2 iOS'),
  link(P_SOFIA, P_DIEGO, 'Onboarding Flutter'),
  link(P_SOFIA, P_ELENA, 'Roadmap de producto'),
  link(P_RENZO, P_ROCIO, 'Migración Swift 6'),
];

const subAreaNameOf = (id: SubAreaId): string =>
  DEMO_SUB_AREAS.find((s) => s.id === id)?.name ?? '—';

const nameOf = (id: PersonId): string =>
  DEMO_ALL_PEOPLE.find((p) => p.id === id)?.displayName ?? '—';

const makeRequest = (params: {
  id: string;
  folio: string;
  personId: PersonId;
  subAreaId: SubAreaId;
  start: string;
  end: string;
  businessDays: number;
  status: VacationRequest['status'];
  replacementPersonId?: PersonId | null;
  /** Qué contestó el respaldo. Omitido = todavía no se le preguntó. */
  replacementDecision?: ReplacementDecision | null;
  replacementReason?: string | null;
  requiredSignatures?: 1 | 2;
  pdfPath?: string | null;
  resolutionReason?: string | null;
  notes?: string;
  /** Days between sending and the decision; feeds «Aprobación media». */
  decidedAfterDays?: number;
  /** Quién la registró por cuenta ajena. Omitido = la pidió su titular. */
  assignedBy?: PersonId;
}): VacationRequest => ({
  ...audit,
  /* Casi todas las pidió cada quien; la registrada por el área lleva `assignedBy`
     y es la única que enseña el botón de borrar. */
  assignedByName: params.assignedBy === undefined ? null : nameOf(params.assignedBy),
  assignedByPersonId: params.assignedBy ?? null,
  id: asId<'RequestId'>(params.id),
  folio: params.folio,
  personId: params.personId,
  personName: nameOf(params.personId),
  areaId: AREA_TECNOLOGIA,
  subAreaId: params.subAreaId,
  subAreaName: subAreaNameOf(params.subAreaId),
  replacementDecision: params.replacementDecision ?? null,
  replacementReason: params.replacementReason ?? null,
  replacementDecidedAt: null,
  replacementWaivedReason: null,
  range: DateRange.fromISOOrThrow(params.start, params.end),
  businessDays: params.businessDays,
  replacementPersonId: params.replacementPersonId ?? null,
  replacementName:
    params.replacementPersonId === undefined || params.replacementPersonId === null
      ? ''
      : nameOf(params.replacementPersonId),
  status: params.status,
  pdfPath: params.pdfPath ?? null,
  notes: params.notes ?? '',
  requiredSignatures: params.requiredSignatures ?? 1,
  resolutionReason: params.resolutionReason ?? null,
  decidedAt:
    params.decidedAfterDays === undefined
      ? null
      : new Date(audit.createdAt.getTime() + params.decidedAfterDays * 86_400_000),
  rescheduledFromFolio: null,
  submittedAt: stamp('2026-07-14'),
});

/** The handoff's own folios and dates, so screens match the design row for row. */
export const DEMO_REQUESTS: readonly VacationRequest[] = [
  /*
   * La que se quedó sin decisión y ya le pasaron las fechas.
   *
   * Existe para que el caso se pueda ver: la tarjeta lo dice en ámbar y ofrece
   * «Resolver», que es el único camino que le queda. Sin una así en el demo, la
   * única forma de mirar esa pantalla era esperar a que en producción se atorara
   * otra — que es exactamente como se descubrió el 18 sep 2026.
   */
  makeRequest({
    id: 'r-0212',
    folio: 'VC-0212',
    personId: P_LUCIA,
    subAreaId: SUB_IOS,
    start: '2026-07-20',
    end: '2026-07-22',
    businessDays: 3,
    status: 'Por revisar',
    replacementPersonId: P_BRUNO,
    requiredSignatures: 1,
    notes: 'Puente de julio; nadie la firmó y las fechas ya pasaron.',
  }),
  makeRequest({
    id: 'r-0207',
    folio: 'VC-0207',
    personId: P_MARCO,
    subAreaId: SUB_IOS,
    start: '2026-09-07',
    end: '2026-09-18',
    businessDays: 10,
    status: 'En revisión',
    replacementPersonId: P_BRUNO,
    replacementDecision: 'declined',
    replacementReason: 'Tengo la certificación de Apple Pay esa misma semana.',
    requiredSignatures: 2,
    pdfPath: 'requests/registro-0207.pdf',
    notes: 'Cierre del release 4.2 entregado a Bruno; pendiente la certificación de Apple Pay.',
  }),
  /*
   * Aprobada con una sola firma, que es como se decide casi todo.
   *
   * El demo sólo tenía decididas de dos firmas —una aprobada y una rechazada— así
   * que el bloque «Doble visto bueno» salía siempre y **el caso de una firma no se
   * podía ver en ningún sitio**: ni la línea que dice quién la aprobó, ni su
   * ausencia. En producción manda el otro: VC-0046 pide una y la firmó alguien que
   * ni siquiera es líder de esa persona.
   */
  makeRequest({
    id: 'r-0220',
    folio: 'VC-0220',
    personId: P_BRUNO,
    subAreaId: SUB_IOS,
    start: '2026-07-13',
    end: '2026-07-17',
    businessDays: 5,
    status: 'Aprobada',
    pdfPath: 'requests/registro-0220.pdf',
    decidedAfterDays: 2,
    notes: 'Traspaso del tablero de QA a Iván antes de salir.',
  }),
  /*
   * La única que registró el área, y existe para que se vea el botón de borrar.
   *
   * Sin ella el demo no puede enseñar la función: el botón sólo sale sobre una
   * solicitud con autor, y todas las demás las pidió su titular. Va a nombre de
   * Renato, que es quien actúa como administrador — con otro autor el botón no
   * aparecería, que es justamente la regla.
   */
  makeRequest({
    id: 'r-0230',
    folio: 'VC-0230',
    personId: P_ROCIO,
    subAreaId: SUB_BACKEND,
    start: '2026-06-15',
    end: '2026-06-19',
    businessDays: 5,
    status: 'Aprobada',
    decidedAfterDays: 0,
    requiredSignatures: 1,
    assignedBy: P_RENATO,
    notes: 'Acordadas de viva voz; se registran para que queden en el portal.',
  }),
  makeRequest({
    id: 'r-0192',
    folio: 'VC-0192',
    personId: P_MARCO,
    subAreaId: SUB_IOS,
    start: '2026-07-06',
    end: '2026-07-10',
    businessDays: 5,
    status: 'Aprobada',
    decidedAfterDays: 2,
    replacementPersonId: P_LUCIA,
    /*
     * Una firma, y es la única de Marco que la tiene.
     *
     * Sus cuatro solicitudes pedían dos, así que el demo no podía enseñar el
     * recorrido de cuatro pasos — el que **no** dibuja «Visto bueno 2º líder»— y
     * ése es el caso mayoritario: 17 de las 32 solicitudes de producción. Un demo
     * que sólo enseña la minoría es un demo que esconde justo lo que hay que
     * revisar.
     */
    requiredSignatures: 1,
    pdfPath: 'requests/registro-0192.pdf',
    notes: 'Semana de descanso tras el lanzamiento de la app 4.1.',
  }),
  makeRequest({
    id: 'r-0175',
    folio: 'VC-0175',
    personId: P_MARCO,
    subAreaId: SUB_IOS,
    start: '2026-05-04',
    end: '2026-05-15',
    businessDays: 10,
    status: 'Rechazada',
    decidedAfterDays: 1,
    replacementPersonId: P_ANA,
    requiredSignatures: 2,
    resolutionReason:
      'Coincide con el cierre de mes del área.',
    notes: 'Rechazada: coincide con la auditoría de seguridad del área.',
  }),
  makeRequest({
    id: 'r-0166',
    folio: 'VC-0166',
    personId: P_MARCO,
    subAreaId: SUB_IOS,
    start: '2026-02-16',
    end: '2026-02-20',
    businessDays: 5,
    status: 'Tomada',
    decidedAfterDays: 3,
    replacementPersonId: P_BRUNO,
    requiredSignatures: 2,
    pdfPath: 'requests/registro-0166.pdf',
  }),
  makeRequest({
    id: 'r-0211',
    folio: 'VC-0211',
    personId: P_BRUNO,
    subAreaId: SUB_IOS,
    start: '2026-07-20',
    end: '2026-07-24',
    businessDays: 5,
    status: 'Por revisar',
    replacementPersonId: P_MARCO,
    /*
     * A propósito sin `replacementDecision`: reproduce una solicitud anterior a
     * la función, que es la forma en que apareció el fallo en producción. La
     * bandeja tiene que pedirle respuesta a Marco igual.
     */
    pdfPath: 'requests/registro-0211.pdf',
  }),
  makeRequest({
    id: 'r-0213',
    folio: 'VC-0213',
    personId: P_LUCIA,
    subAreaId: SUB_IOS,
    start: '2026-08-12',
    end: '2026-08-19',
    businessDays: 6,
    status: 'En revisión',
    replacementPersonId: P_ANA,
    requiredSignatures: 2,
    pdfPath: 'requests/registro-0213.pdf',
  }),
  makeRequest({
    id: 'r-0214',
    folio: 'VC-0214',
    personId: P_LUCIA,
    subAreaId: SUB_IOS,
    start: '2026-11-09',
    end: '2026-11-13',
    businessDays: 5,
    status: 'Visto bueno',
    replacementPersonId: P_BRUNO,
    requiredSignatures: 2,
    pdfPath: 'requests/registro-0214.pdf',
  }),
  makeRequest({
    id: 'r-0220',
    folio: 'VC-0220',
    personId: P_DIEGO,
    subAreaId: SUB_FLUTTER,
    start: '2026-07-13',
    end: '2026-07-17',
    businessDays: 5,
    status: 'Aprobada',
    decidedAfterDays: 1,
    replacementPersonId: P_SOFIA,
  }),
  makeRequest({
    id: 'r-0221',
    folio: 'VC-0221',
    personId: P_ROCIO,
    subAreaId: SUB_BACKEND,
    start: '2026-07-27',
    end: '2026-07-31',
    businessDays: 5,
    status: 'Aprobada',
    decidedAfterDays: 2,
    replacementPersonId: P_IVAN,
  }),
  makeRequest({
    id: 'r-0222',
    folio: 'VC-0222',
    personId: P_ELENA,
    subAreaId: SUB_PO,
    start: '2026-07-06',
    end: '2026-07-08',
    businessDays: 3,
    status: 'Aprobada',
    decidedAfterDays: 2,
  }),
];

const approval = (
  id: string,
  requestId: string,
  approverPersonId: PersonId,
  order: 1 | 2,
  decision: RequestApproval['decision'],
  reason: string | null = null,
): RequestApproval => ({
  ...audit,
  id: asId<'ApprovalId'>(id),
  requestId: asId<'RequestId'>(requestId),
  approverPersonId,
  approverName: nameOf(approverPersonId),
  order,
  decision,
  decidedAt: decision === 'pending' ? null : stamp('2026-07-20'),
  reason,
});

export const DEMO_APPROVALS: readonly RequestApproval[] = [
  approval('ap-207-1', 'r-0207', P_ANA, 1, 'pending'),
  approval('ap-207-2', 'r-0207', P_ROCIO, 2, 'pending'),
  approval('ap-220-1', 'r-0220', P_ANA, 1, 'approved'),
  approval('ap-192-1', 'r-0192', P_ANA, 1, 'approved'),
  approval('ap-192-2', 'r-0192', P_ROCIO, 2, 'approved'),
  approval('ap-175-1', 'r-0175', P_ANA, 1, 'rejected', 'Coincide con el cierre de mes del área.'),
  approval('ap-211-1', 'r-0211', P_ANA, 1, 'pending'),
  approval('ap-213-1', 'r-0213', P_ANA, 1, 'pending'),
  approval('ap-213-2', 'r-0213', P_IVAN, 2, 'pending'),
  // The double sign-off mid-flight: first signature in, second still open.
  approval('ap-214-1', 'r-0214', P_ANA, 1, 'approved'),
  approval('ap-214-2', 'r-0214', P_IVAN, 2, 'pending'),
];

const workItem = (id: string, requestId: string, ticket: string, text: string): RequestWorkItem => ({
  ...audit,
  id: asId<'WorkItemId'>(id),
  requestId: asId<'RequestId'>(requestId),
  ticket,
  text,
});

export const DEMO_WORK_ITEMS: readonly RequestWorkItem[] = [
  workItem('w1', 'r-0207', 'IOS-482', 'Checkout con Apple Pay — en QA, falta certificar sandbox'),
  workItem('w2', 'r-0211', 'IOS-501', 'Widgets de bloqueo, en code review'),
  workItem('w3', 'r-0211', 'IOS-498', 'Migración a Swift 6, 60% avanzado'),
  workItem('w4', 'r-0213', 'IOS-476', 'Push notifications desplegado, monitoreando'),
  workItem('w5', 'r-0214', 'QA-233', 'Certificación de la 4.2 lista para regresión'),
];

const permission = (params: {
  id: string;
  folio: string;
  personId: PersonId;
  subAreaId: SubAreaId;
  kind: string;
  start: string;
  end: string;
  status: PermissionRequest['status'];
  withPay: boolean;
  reason: string;
  hours?: { from: string; to: string } | null;
}): PermissionRequest => ({
  ...audit,
  id: asId<'PermissionRequestId'>(params.id),
  folio: params.folio,
  personId: params.personId,
  personName: nameOf(params.personId),
  areaId: AREA_TECNOLOGIA,
  subAreaId: params.subAreaId,
  subAreaName: subAreaNameOf(params.subAreaId),
  kind: params.kind,
  range: DateRange.fromISOOrThrow(params.start, params.end),
  hours: params.hours === undefined || params.hours === null
    ? null
    : { ...params.hours, earlyHours: null },
  withPay: params.withPay,
  reason: params.reason,
  status: params.status,
  decidedByPersonId: params.status === 'Por aprobar' ? null : P_ANA,
  decidedByName: params.status === 'Por aprobar' ? '' : nameOf(P_ANA),
  decidedAt: params.status === 'Por aprobar' ? null : stamp('2026-07-22'),
  resolutionReason:
    params.status === 'Rechazado' ? 'El equipo necesita presencia en sitio esos días.' : null,
});

export const DEMO_PERMISSIONS: readonly PermissionRequest[] = [
  permission({
    id: 'pr-0781',
    folio: 'PER-0781',
    personId: P_MARCO,
    subAreaId: SUB_IOS,
    kind: 'Home office',
    start: '2026-07-29',
    end: '2026-07-31',
    status: 'Aprobado',
    withPay: true,
    reason: 'Trabajo remoto por mudanza; disponible en horario completo.',
  }),
  permission({
    id: 'pr-0784',
    folio: 'PER-0784',
    personId: P_LUCIA,
    subAreaId: SUB_IOS,
    kind: 'Permiso de horas',
    start: '2026-07-28',
    end: '2026-07-28',
    status: 'Por aprobar',
    withPay: true,
    reason: 'Cita médica de 9:00 a 12:00; recupera por la tarde.',
    hours: { from: '09:00', to: '12:00' },
  }),
  permission({
    id: 'pr-0786',
    folio: 'PER-0786',
    personId: P_BRUNO,
    subAreaId: SUB_IOS,
    kind: 'Home office',
    start: '2026-08-03',
    end: '2026-08-07',
    status: 'Por aprobar',
    withPay: true,
    reason: 'Semana remota para cerrar la migración a Swift 6 sin interrupciones.',
  }),
  permission({
    id: 'pr-0770',
    folio: 'PER-0770',
    personId: P_DIEGO,
    subAreaId: SUB_FLUTTER,
    kind: 'Día personal',
    start: '2026-07-17',
    end: '2026-07-17',
    status: 'Aprobado',
    withPay: true,
    reason: 'Trámite escolar de su hija.',
  }),
  permission({
    id: 'pr-0765',
    folio: 'PER-0765',
    personId: P_ROCIO,
    subAreaId: SUB_BACKEND,
    kind: 'Permiso sin goce',
    start: '2026-06-22',
    end: '2026-06-26',
    status: 'Rechazado',
    withPay: false,
    reason: 'Coincidía con el corte de infraestructura del cierre de mes.',
  }),
  permission({
    id: 'pr-0788',
    folio: 'PER-0788',
    personId: P_ELENA,
    subAreaId: SUB_PO,
    kind: 'Día personal',
    start: '2026-07-30',
    end: '2026-07-30',
    status: 'Por aprobar',
    withPay: true,
    reason: 'Asuntos personales; deja el backlog priorizado.',
  }),
];

const incapacity = (params: {
  id: string;
  folio: string;
  personId: PersonId;
  subAreaId: SubAreaId;
  kind: string;
  start: string;
  end: string;
  status: Incapacity['status'];
  imssFolio: string;
  subsidy: string;
  coverage: string;
  notes: string;
  observation?: string | null;
}): Incapacity => ({
  ...audit,
  id: asId<'IncapacityId'>(params.id),
  folio: params.folio,
  personId: params.personId,
  personName: nameOf(params.personId),
  areaId: AREA_TECNOLOGIA,
  subAreaId: params.subAreaId,
  subAreaName: subAreaNameOf(params.subAreaId),
  kind: params.kind,
  range: DateRange.fromISOOrThrow(params.start, params.end),
  /*
   * Derived, not declared.
   *
   * This used to be a hand-written parameter and one of the five was wrong — a
   * maternity leave counted 84 where its own dates say 85, which then understated
   * the period KPI. A denormalised count nobody checks against its source is a
   * bug waiting for someone to trust it.
   */
  naturalDays: DateRange.fromISOOrThrow(params.start, params.end).naturalDays,
  imssFolio: params.imssFolio,
  umf: params.imssFolio.length > 0 ? '33' : '',
  subsidy: params.subsidy,
  coverage: params.coverage,
  certificatePath: params.imssFolio.length > 0 ? 'incapacities/certificado.pdf' : null,
  status: params.status,
  notes: params.notes,
  validatedByPersonId: params.status === 'Validada' ? P_RENATO : null,
  validatedAt: params.status === 'Validada' ? stamp('2026-07-15') : null,
  observation: params.observation ?? null,
});

export const DEMO_INCAPACITIES: readonly Incapacity[] = [
  incapacity({
    id: 'inc-3391',
    folio: 'INC-3391',
    personId: P_MARCO,
    subAreaId: SUB_IOS,
    kind: 'Enfermedad general',
    start: '2026-06-15',
    end: '2026-06-19',
    status: 'Validada',
    imssFolio: 'IMSS-3391',
    subsidy: 'Subsidio 60%',
    coverage: 'Bruno Chávez asumió el release 4.1',
    notes: 'Reposo indicado por cinco días; retorno con alta médica.',
  }),
  incapacity({
    id: 'inc-3402',
    folio: 'INC-3402',
    personId: P_ROCIO,
    subAreaId: SUB_BACKEND,
    kind: 'Riesgo de trabajo',
    start: '2026-07-13',
    end: '2026-07-24',
    status: 'Validada',
    imssFolio: 'IMSS-3402',
    subsidy: 'Subsidio 100%',
    coverage: 'Iván Mora cubre guardias de infraestructura',
    notes: 'Lesión durante traslado laboral; el IMSS cubre el 100% del salario.',
  }),
  /*
   * Una propia sin validar todavía, que es lo que hace visible el filtro «Por
   * validar» y el botón de retirar en la sesión de empleado del demo. Sin ella
   * ambos existían y nunca se podían ver.
   */
  incapacity({
    id: 'inc-3418',
    folio: 'INC-3418',
    personId: P_MARCO,
    subAreaId: SUB_IOS,
    kind: 'Enfermedad general',
    start: '2026-08-24',
    end: '2026-08-25',
    status: 'Por validar',
    imssFolio: '',
    subsidy: 'Pendiente',
    coverage: 'Bruno Chávez cubre la revisión de PRs',
    notes: 'Dos días de reposo; falta subir el certificado sellado.',
  }),
  incapacity({
    id: 'inc-3410',
    folio: 'INC-3410',
    personId: P_SOFIA,
    subAreaId: SUB_FLUTTER,
    kind: 'Enfermedad general',
    start: '2026-07-27',
    end: '2026-07-29',
    status: 'Por validar',
    imssFolio: '',
    subsidy: 'Pendiente',
    coverage: 'Diego Salas revisa los PR abiertos',
    notes: 'Reportó incapacidad por vía telefónica; falta subir el documento sellado.',
  }),
  incapacity({
    id: 'inc-3415',
    folio: 'INC-3415',
    personId: P_ELENA,
    subAreaId: SUB_PO,
    kind: 'Maternidad',
    start: '2026-08-03',
    end: '2026-10-26',
    status: 'Programada',
    imssFolio: 'IMSS-3415',
    subsidy: 'Subsidio 100%',
    coverage: 'Ana Cifuentes asume el backlog de producto',
    notes: '84 días naturales de licencia: 42 antes y 42 después del parto.',
  }),
  incapacity({
    id: 'inc-3372',
    folio: 'INC-3372',
    personId: P_BRUNO,
    subAreaId: SUB_IOS,
    kind: 'Enfermedad general',
    start: '2026-04-06',
    end: '2026-04-08',
    status: 'Observada',
    imssFolio: 'IMSS-3372',
    subsidy: 'En revisión',
    coverage: 'Marco Rivas atendió incidencias',
    notes: 'RR.HH. solicitó reenviar el certificado con el sello legible.',
    observation: 'El certificado no viene sellado por el IMSS.',
  }),
];

/**
 * El saldo del periodo que corre para esa persona, no el del año 2026.
 *
 * El demo también cuenta por aniversario: Ana renueva el 22 de agosto y Bruno el
 * 8 de noviembre, así que el 27 de julio —el hoy congelado del demo— los dos
 * están en su periodo de 2025. Etiquetarlos todos como «2026» hacía que sus
 * saldos no aparecieran en ninguna pantalla, que es exactamente el fallo que
 * este cambio corrige en producción.
 */
const balance = (
  personId: PersonId,
  entitled: number,
  taken: number,
  scheduled: number,
  pending: number,
  expiring = 0,
  /** 1 para el periodo anterior, que con 18 meses puede seguir vigente. */
  periodsBack = 0,
): Balance => {
  const hire = DEMO_ALL_PEOPLE.find((p) => p.id === personId)?.hireDate ?? null;
  const period = (hire === null ? DEMO_TODAY.year : periodYearOf(hire, DEMO_TODAY)) - periodsBack;
  /* La fecha de vencimiento es el final de su propio periodo, no una escrita a
     mano: si el fixture y la política discreparan, el demo enseñaría un aviso
     que producción nunca daría. */
  const expiresOn = hire === null || expiring <= 0 ? null : periodFor(hire, DEMO_TODAY).lastDay;
  return ({
  ...audit,
  id: asId<'BalanceId'>(`${personId}_${String(period)}`),
  personId,
  personName: nameOf(personId),
  areaId: AREA_TECNOLOGIA,
  year: period,
  entitledDays: entitled,
  openingUsedDays: 0,
  carriedDebtDays: 0,
  takenDays: taken,
  scheduledDays: scheduled,
  pendingDays: pending,
  expiringDays: expiring,
  expiresOn,
  remainingDays: Math.max(0, entitled - taken - scheduled - pending),
  });
};

/**
 * Marco tiene dos periodos vigentes: seis días del 2025 que vencen pronto —lo que
 * le da algo que enseñar al aviso de Inicio— y su periodo 2026 completo.
 */
export const DEMO_BALANCES: readonly Balance[] = [
  balance(P_MARCO, 28, 22, 0, 0, 6, 1),
  balance(P_MARCO, 30, 4, 0, 10),
  balance(P_LUCIA, 12, 2, 0, 11),
  balance(P_ANA, 24, 6, 0, 0),
  balance(P_BRUNO, 16, 2, 0, 5),
  balance(P_DIEGO, 20, 5, 0, 0),
  balance(P_SOFIA, 26, 5, 0, 0),
  balance(P_ROCIO, 22, 11, 5, 0),
  balance(P_IVAN, 22, 7, 0, 0),
  balance(P_ELENA, 24, 3, 0, 0),
  balance(P_RENATO, 26, 4, 0, 0),
  balance(P_RENATA, 26, 8, 0, 0),
];

const contact = (
  id: string,
  personId: PersonId,
  name: string,
  relation: string,
  phone: string,
  order: number,
): EmergencyContact => ({
  ...audit,
  id: asId<'EmergencyContactId'>(id),
  personId,
  areaId: AREA_TECNOLOGIA,
  name,
  relation,
  phone,
  preferenceOrder: order,
});

export const DEMO_CONTACTS: readonly EmergencyContact[] = [
  contact('ec-1', P_MARCO, 'Lucía Rivas', 'Esposa', '+52 55 4821 7730', 0),
  contact('ec-2', P_MARCO, 'Ernesto Rivas', 'Padre', '+52 55 1194 2208', 1),
  contact('ec-3', P_ANA, 'Raúl Cifuentes', 'Padre', '+52 55 7781 2043', 0),
  contact('ec-4', P_ANA, 'Nora Vela', 'Amiga', '+52 55 9902 3315', 1),
  contact('ec-5', P_BRUNO, 'Alma Duarte', 'Madre', '+52 55 8871 4409', 0),
  // Only one contact: the profile screen must show its "falta un contacto" warning.
  contact('ec-6', P_LUCIA, 'Marta Fernández', 'Madre', '+52 33 7712 6650', 0),
];

const entry = (
  id: string,
  kind: AuditEntry['kind'],
  actorPersonId: PersonId | null,
  actorName: string,
  action: string,
  detail: string,
  createdAt: string,
  documentPath: string | null = null,
  /**
   * A movement whose reach is the whole company rather than one area.
   *
   * It matters which of the two a change is recorded as. Scoped to an area, a
   * company-wide switch is invisible to the superuser who owns it and shows up
   * in one area administrator's log as though it were theirs to answer for.
   */
  scope: 'area' | 'global' = 'area',
): AuditEntry => ({
  id: asId<'AuditEntryId'>(id),
  areaId: scope === 'global' ? asId<'AreaId'>(GLOBAL_AREA_SENTINEL) : AREA_TECNOLOGIA,
  areaName: scope === 'global' ? 'Global' : 'Tecnología',
  kind,
  actorPersonId,
  actorName,
  action,
  detail,
  documentPath,
  targetPath: null,
  createdAt: stamp(createdAt),
  updatedAt: stamp(createdAt),
  schemaVersion: 1,
});

export const DEMO_AUDIT: readonly AuditEntry[] = [
  entry('au-1', 'Aprobación', P_ANA, 'Ana Cifuentes', 'Aprobó VC-0211 de Bruno Chávez',
    '20 – 24 jul · 5 días hábiles. Reemplazo: Marco Rivas (confirmado).', '2026-07-27'),
  entry('au-2', 'Documento', P_LUCIA, 'Lucía Fernández', 'Adjuntó el registro de vacaciones de VC-0213',
    'Formato LFT firmado, validado por RR.HH.', '2026-07-27', 'registro-0213.pdf'),
  entry('au-3', 'Política', P_RENATA, 'Renata Ochoa (sistema)',
    'Activó el módulo de home office para todas las áreas', 'Alcance global: 5 áreas y sus sub áreas.',
    '2026-07-27', null, 'global'),
  entry('au-4', 'Solicitud', P_SOFIA, 'Sofía Ballón', 'Envió la solicitud VC-0209',
    '1 – 4 sep · 4 días hábiles. Falta el PDF firmado.', '2026-07-26'),
  entry('au-5', 'Política', P_RENATO, 'Renato Vidal (RR.HH.)',
    'Actualizó el máximo de personas fuera en Backend', 'De 2 a 3 personas simultáneas por sub área.', '2026-07-26'),
  entry('au-6', 'Cuenta', P_RENATO, 'Renato Vidal (RR.HH.)', 'Validó la cuenta de Renzo Aguilar',
    'raguilar@knotion.com · sub área Backend.', '2026-07-24'),
  entry('au-7', 'Aprobación', P_ROCIO, 'Rocío Paz', 'Rechazó VC-0175 de Marco Rivas',
    'Coincide con la auditoría de seguridad del área.', '2026-07-24', 'dictamen-0175.pdf'),
  entry('au-8', 'Documento', P_RENATO, 'RR.HH.', 'Archivó el expediente del primer semestre',
    '14 solicitudes cerradas, 12 documentos validados.', '2026-07-22', 'expediente-s1-2026.pdf'),
  entry('au-9', 'Liderazgo', P_RENATO, 'Renato Vidal (RR.HH.)',
    'Asignó a Iván Mora como líder de Lucía Fernández', 'Segundo visto bueno para su sub área.', '2026-07-21'),
  entry('au-10', 'Solicitud', P_BRUNO, 'Bruno Chávez', 'Envió la solicitud VC-0211',
    'Designó a Marco Rivas como reemplazo.', '2026-07-18', 'registro-0211.pdf'),
  entry('au-11', 'Política', P_RENATA, 'Renata Ochoa (sistema)', 'Publicó el calendario de feriados 2026',
    '11 días de descanso (obligatorios LFT y por costumbre).', '2026-07-15', 'feriados-2026.pdf', 'global'),
];

/** Holidays computed from the same LFT rules the seeder uses. */
export const DEMO_HOLIDAYS: readonly Holiday[] = [2026, 2027].flatMap((year) =>
  [...statutoryHolidaysFor(year), ...customaryHolidaysFor(year)].map((h) => ({
    ...audit,
    id: asId<'HolidayId'>(`knotion_${h.date}`),
    companyId: DEMO_COMPANY.id,
    year,
    date: CalendarDate.fromISOOrThrow(h.date),
    name: h.name,
    type: h.type,
    enabled: true,
  })),
);
